import React from 'react';
import {
  Box,
  Grid,
  GridProps,
  LinearProgress,
  Typography,
} from '@mui/material';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { SPARK_ROUTES } from 'constants/routes';
import { Close } from '@mui/icons-material';
import { BreathingSparkAnimation } from 'components/Animations';
import { ErrorStatusIcon, SuccessStatusIcon } from 'components/SVG';
import { useStatusBarStore } from 'stores';
import { SparkButton } from 'sharedComponents/UI';
import { useSparkRunsQueueJob } from 'hooks/SparkRuns';
import { useCurrentRoute } from 'hooks/common';
type SparkRunStatusProps = {
  containerStyles?: GridProps['sx'];
  renderInHeader?: boolean;
};
function SparkRunStatus({
  containerStyles,
  renderInHeader = false,
}: SparkRunStatusProps) {
  const [collapseStatusBar, setCollapseStatusBar] = React.useState(false);
  const navigate = useNavigate();
  const { isSparkRun } = useCurrentRoute();
  const isVisible = useStatusBarStore(
    React.useCallback((state) => state.isVisible, [])
  );
  const isComplete = useStatusBarStore(
    React.useCallback((state) => state.isComplete, [])
  );
  const isRunning = useStatusBarStore(
    React.useCallback((state) => state.isRunning, [])
  );
  const isError = useStatusBarStore(
    React.useCallback((state) => state.isError, [])
  );
  const isPendingPayment = useStatusBarStore(
    React.useCallback((state) => state.isPendingPayment, [])
  );
  const statusBarID = useStatusBarStore(
    React.useCallback((state) => state.statusBarID, [])
  );
  const resetStatusBarStore = useStatusBarStore(
    React.useCallback((state) => state.resetStatusBarStore, [])
  );
  const updateStatues = useStatusBarStore(
    React.useCallback((state) => state.updateStatues, [])
  );
  const { percentageComplete } = useSparkRunsQueueJob(
    statusBarID,
    !isPendingPayment
  );

  React.useEffect(() => {
    let timer: NodeJS.Timeout;
    if (isVisible && isComplete) {
      timer = setTimeout(() => {
        setCollapseStatusBar(true);
        setTimeout(() => {
          updateStatues({ isVisible: false });
        }, 500);
      }, 10000);
    }

    return () => clearTimeout(timer);
  }, [isComplete, isVisible, updateStatues]);

  const handleClose = () => {
    updateStatues({ isVisible: false });
  };
  if (!isVisible || isPendingPayment || renderInHeader) {
    return null;
  }

  const handleViewClick = () => {
    navigate(`${SPARK_ROUTES.SPARK_RUN_DETAIL}/${statusBarID}`);
    resetStatusBarStore();
  };
  return (
    <Grid
      item
      xs={12}
      bgcolor='white'
      borderBottom={(theme) => `1px solid ${theme.palette.borderColor}`}
      sx={containerStyles}
      component={motion.div}
      initial={{ height: '4rem', opacity: 1 }}
      animate={{
        height: collapseStatusBar ? 0 : '4rem',
        opacity: collapseStatusBar ? 0 : 1,
      }}
      exit={{ height: 0, opacity: 0 }}
      transition={{ duration: 0.5 }}
      style={{ transformOrigin: 'top' }}
    >
      {isError && (
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1,
          }}
        >
          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center' }}>
            <ErrorStatusIcon width={28} height={28} />
            <Typography
              variant='h5'
              sx={{
                fontFamily: 'Mont-SemiBold',
                fontSize: '20px',
                color: '#000000',
                ml: 2,
              }}
            >
              Spark Run Error
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            {!isSparkRun && (
              <SparkButton
                variant='contained'
                sx={{ mr: 2 }}
                onClick={handleViewClick}
              >
                View
              </SparkButton>
            )}
            <Close
              sx={{ cursor: 'pointer', color: '#000000' }}
              onClick={handleClose}
            />
          </Box>
        </Grid>
      )}

      {isComplete && (
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            py: 1,
          }}
        >
          <Box sx={{ ml: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <SuccessStatusIcon width='28' height='28' />
            <Typography
              variant='h5'
              sx={{
                fontFamily: 'Mont-SemiBold',
                fontSize: '20px',
                color: '#000000',
              }}
            >
              Spark Run Completed
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
            {!isSparkRun && (
              <SparkButton
                variant='contained'
                sx={{ mr: 2 }}
                onClick={handleViewClick}
              >
                View
              </SparkButton>
            )}
            <Close
              sx={{ cursor: 'pointer', color: '#000000' }}
              onClick={handleClose}
            />
          </Box>
        </Grid>
      )}

      {isRunning && (
        <Grid
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 1,
          }}
        >
          <Box sx={{ ml: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <BreathingSparkAnimation width={30} height={30} />
            <Typography
              variant='h5'
              sx={{
                fontFamily: 'Mont-SemiBold',
                fontSize: '20px',
                color: '#000000',
              }}
            >
              Running Spark
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              mr: 2,
              width: '20%',
              gap: 1,
            }}
          >
            <Box sx={{ width: '100%' }}>
              <LinearProgress
                sx={{ height: '8px', borderRadius: '10px' }}
                variant='determinate'
                value={percentageComplete}
              />
            </Box>
            {!isSparkRun && (
              <Close
                sx={{ cursor: 'pointer', color: '#000000' }}
                onClick={handleClose}
              />
            )}
          </Box>
        </Grid>
      )}
    </Grid>
  );
}

export default SparkRunStatus;
